import React, { useEffect } from 'react';
import { Control } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { Button } from '@givebutter/primitives/button';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { faBell, faBellSlash, faFlag } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateContactsSubscriptions } from 'api/contacts/contacts';
import * as Dialog from 'components/dialog';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Contact } from 'types/domain';

interface UpdateContactNotificationsModalLocationState {
    contacts: Contact[];
}

type NotificationSettingOption = 'subscribe' | 'unsubscribe' | 'none';
type NotificationSettingFieldName = 'email' | 'phone' | 'mail';
type NotificationSettingPermissionFieldName =
    | 'emailPermission'
    | 'phonePermission'
    | 'mailPermission';
type UpdateContactNotificationsFormValues = {
    [key in NotificationSettingFieldName]: NotificationSettingOption;
} & {
    [key in NotificationSettingPermissionFieldName]: boolean;
};

const UpdateContactNotificationsModal = () => {
    const toast = useToast();
    const { goBack } = useDialogRoute();
    const queryClient = useQueryClient();
    const location = useLocation<UpdateContactNotificationsModalLocationState | undefined>();
    const { contacts = [] } = location.state || {};
    const { accountId } = useCurrentAccount();

    useEffect(() => {
        if (contacts.length === 0) goBack();
    }, [contacts, goBack]);

    const methods = Form.useGivebutterForm<UpdateContactNotificationsFormValues>({
        defaultValues: {
            email: 'none',
            phone: 'none',
            mail: 'none',
            emailPermission: false,
            phonePermission: false,
            mailPermission: false,
        },
    });
    const {
        control,
        handleSubmit,
        watch,
        resetFormButton,
        formState: { isDirty, isValid },
    } = methods;
    const [email, phone, mail] = watch(['email', 'phone', 'mail']);

    const { mutate: submit, isPending: isLoading } = useMutation({
        mutationFn: () => {
            return updateContactsSubscriptions(accountId, {
                is_email_subscribed: email === 'none' ? null : email === 'subscribe',
                is_phone_subscribed: phone === 'none' ? null : phone === 'subscribe',
                is_address_subscribed: mail === 'none' ? null : mail === 'subscribe',
                emails: contacts.map(item => item.primary_email).filter(Boolean),
                phones: contacts.map(item => item.primary_phone).filter(Boolean),
                contact_ids: contacts.map(item => item.id),
            });
        },
        onSuccess: () => {
            toast.success('Contact Engage Subscription settings updated.');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.contacts(accountId) });
            goBack();
        },
        onError: () => {
            toast.error(
                <React.Fragment>
                    <span className="text-semibold">Whoops.</span> Something went wrong. Please try
                    again.
                </React.Fragment>,
            );
            resetFormButton();
        },
    });

    return (
        <Dialog.Body className="flex flex-col gap-4 pt-4">
            <Dialog.Close />
            <Dialog.Title>Subscriptions</Dialog.Title>
            <Dialog.Description className="font-light">
                These subscription settings are for <strong>Engage</strong> messages only, and will
                not affect system messages such as email receipts. This will apply to all selected
                contacts.
            </Dialog.Description>
            <Form.Provider {...methods}>
                <form onSubmit={handleSubmit(() => submit())} className="flex flex-col gap-8">
                    <Form.Container className="gap-6">
                        <div className="flex flex-col gap-4">
                            <div>
                                <Form.Label name="email">Email</Form.Label>
                                <SubscriptionSettingSelect control={control} name="email" />
                            </div>
                            {email === 'subscribe' && (
                                <SubscriptionSettingConfirmation
                                    control={control}
                                    label="Email"
                                    name="emailPermission"
                                    parentSettingName="email"
                                />
                            )}
                        </div>
                        <div className="flex flex-col gap-4">
                            <div>
                                <Form.Label name="phone">SMS</Form.Label>
                                <SubscriptionSettingSelect control={control} name="phone" />
                            </div>
                            {phone === 'subscribe' && (
                                <SubscriptionSettingConfirmation
                                    control={control}
                                    label="SMS"
                                    name="phonePermission"
                                    parentSettingName="phone"
                                />
                            )}
                        </div>
                        <div className="flex flex-col gap-4">
                            <div>
                                <Form.Label name="phone">Mail</Form.Label>
                                <SubscriptionSettingSelect control={control} name="mail" />
                            </div>
                            {mail === 'subscribe' && (
                                <SubscriptionSettingConfirmation
                                    control={control}
                                    label="Mail"
                                    name="mailPermission"
                                    parentSettingName="mail"
                                />
                            )}
                        </div>
                    </Form.Container>
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" color="neutral" onClick={() => goBack()}>
                            Cancel
                        </Button>
                        <Form.SubmitButton isSubmitting={isLoading} disabled={!isDirty || !isValid}>
                            Save
                        </Form.SubmitButton>
                    </div>
                </form>
            </Form.Provider>
        </Dialog.Body>
    );
};

const SubscriptionSettingSelect = ({
    control,
    name,
}: {
    control: Control<UpdateContactNotificationsFormValues, any>;
    name: NotificationSettingFieldName;
}) => {
    return (
        <Form.SingleSelect control={control} name={name}>
            <SingleSelect.Trigger aria-label="Subscription setting" />
            <SingleSelect.Content>
                <SingleSelect.Item value="none">
                    <FontAwesomeIcon icon={faFlag} />
                    <SingleSelect.ItemText>Keep current preference</SingleSelect.ItemText>
                </SingleSelect.Item>
                <SingleSelect.Item value="subscribe">
                    <FontAwesomeIcon icon={faBell} />
                    <SingleSelect.ItemText>Subscribe</SingleSelect.ItemText>
                </SingleSelect.Item>
                <SingleSelect.Item value="unsubscribe">
                    <FontAwesomeIcon icon={faBellSlash} />
                    <SingleSelect.ItemText>Unsubscribe</SingleSelect.ItemText>
                </SingleSelect.Item>
            </SingleSelect.Content>
        </Form.SingleSelect>
    );
};

const SubscriptionSettingConfirmation = ({
    control,
    name,
    parentSettingName,
    label,
}: {
    control: Control<UpdateContactNotificationsFormValues, any>;
    label: 'SMS' | 'Email' | 'Mail';
    name: NotificationSettingPermissionFieldName;
    parentSettingName: NotificationSettingFieldName;
}) => {
    return (
        <AlertNotification.Root type="info" closable={false}>
            <AlertNotification.Icon />
            <AlertNotification.Content>
                <AlertNotification.Title>
                    {parentSettingName === 'mail' ? 'Letters' : `Messages`} will be sent their
                    primary {parentSettingName === 'mail' ? 'address' : parentSettingName}.
                </AlertNotification.Title>
                <AlertNotification.Description>
                    You are about to subscribe the selected contacts to receive{' '}
                    {parentSettingName === 'mail' ? 'letters' : `${label} messages`} from Engage. To
                    proceed, please confirm they have given you permission to do so.
                </AlertNotification.Description>
                <div className="flex items-center gap-2">
                    <Form.UseFormCheckboxInput
                        name={name}
                        control={control}
                        rules={{
                            validate: (value, formValues) => {
                                return (
                                    formValues[parentSettingName] === 'subscribe' && value === true
                                );
                            },
                        }}
                    />
                    <Form.UseFormLabel name={name} className="cursor-pointer text-gray-700">
                        I confirm they have given me permission.
                    </Form.UseFormLabel>
                </div>
            </AlertNotification.Content>
        </AlertNotification.Root>
    );
};

export { UpdateContactNotificationsModal };
export type { UpdateContactNotificationsModalLocationState };
